/**
 * Geometry for the virtualized PDF page stack in ANNPDFViewer.
 *
 * Every page gets its own box (top/width/height), so documents with mixed page
 * sizes, landscape scans and user-rotated pages scroll and jump correctly.
 */

/** Unscaled page size with the PDF's own /Rotate applied, plus that intrinsic rotation. */
export type PdfPageSize = { width: number; height: number; rotate: number };

export type PdfPageBox = { top: number; width: number; height: number; scale: number };

export type PdfLayout = {
   /** boxes[0] is page 1. `top` is relative to the top of the page stack. */
   boxes: PdfPageBox[];
   totalHeight: number;
   maxWidth: number;
};

export type PdfZoomMode = "fitWidth" | "manual";

/** Vertical gap between pages. */
export const PDF_VIEWER_PAGE_GAP = 40;
/** Room above page 1 for the "AI suggested" badge. */
export const PDF_VIEWER_STACK_TOP_PAD = 32;
export const PDF_VIEWER_STACK_END_PAD = 48;
/** Width of the selected/suggested outline drawn around a page frame. */
export const PDF_VIEWER_FRAME_OUTLINE = 4;
export const PDF_VIEWER_MIN_SCALE = 0.35;
export const PDF_VIEWER_MAX_SCALE = 3;

export const EMPTY_PDF_LAYOUT: PdfLayout = { boxes: [], totalHeight: 0, maxWidth: 0 };

export function clampPdfScale(scale: number): number {
   return Math.max(PDF_VIEWER_MIN_SCALE, Math.min(PDF_VIEWER_MAX_SCALE, scale));
}

export function fitPageWidthScale(containerWidth: number, pageWidth: number, padding = 32): number {
   if (containerWidth <= 0 || pageWidth <= 0) {
      return 1;
   }
   return clampPdfScale((containerWidth - padding) / pageWidth);
}

export function normalizeRotation(degrees: number): 0 | 90 | 180 | 270 {
   const quarterTurns = Math.round(degrees / 90);
   return ((((quarterTurns % 4) + 4) % 4) * 90) as 0 | 90 | 180 | 270;
}

/** Size of a page after an extra user rotation on top of its intrinsic one. */
export function rotatedPageSize(size: PdfPageSize, userRotation: number): { width: number; height: number } {
   return normalizeRotation(userRotation) % 180 === 0
      ? { width: size.width, height: size.height }
      : { width: size.height, height: size.width };
}

export function buildPdfLayout(args: {
   sizes: readonly PdfPageSize[];
   rotations: Readonly<Record<number, number>>;
   zoomMode: PdfZoomMode;
   manualScale: number;
   /** Width available for a page, excluding outline and padding. */
   availableWidth: number;
}): PdfLayout {
   const { sizes, rotations, zoomMode, manualScale, availableWidth } = args;
   const boxes: PdfPageBox[] = [];
   let y = PDF_VIEWER_STACK_TOP_PAD;
   let maxWidth = 0;
   for (let index = 0; index < sizes.length; index++) {
      const size = rotatedPageSize(sizes[index], rotations[index + 1] ?? 0);
      const scale = zoomMode === "fitWidth"
         ? fitPageWidthScale(availableWidth, size.width, 0)
         : clampPdfScale(manualScale);
      const width = size.width * scale;
      const height = size.height * scale;
      boxes.push({ top: y, width, height, scale });
      maxWidth = Math.max(maxWidth, width);
      y += height + PDF_VIEWER_PAGE_GAP;
   }
   const totalHeight = boxes.length > 0
      ? y - PDF_VIEWER_PAGE_GAP + PDF_VIEWER_STACK_END_PAD
      : 0;
   return { boxes, totalHeight, maxWidth };
}

/**
 * 1-based page whose slot (page plus the gap below it) contains `offset`.
 * Offsets above page 1 map to page 1, offsets past the end to the last page.
 */
export function pageAtOffset(layout: PdfLayout, offset: number): number {
   const { boxes } = layout;
   if (boxes.length === 0) {
      return 1;
   }
   let lo = 0;
   let hi = boxes.length - 1;
   while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      if (boxes[mid].top <= offset) {
         lo = mid;
      } else {
         hi = mid - 1;
      }
   }
   return lo + 1;
}

/** Inclusive 1-based page range intersecting [from, to]. */
export function pageRangeForOffsets(layout: PdfLayout, from: number, to: number): { first: number; last: number } {
   return { first: pageAtOffset(layout, from), last: pageAtOffset(layout, to) };
}

/** Position within a page slot, used to keep the view steady when the layout changes. */
export type PdfScrollAnchor = { page: number; fraction: number };

export function anchorForOffset(layout: PdfLayout, offset: number): PdfScrollAnchor | null {
   if (layout.boxes.length === 0) {
      return null;
   }
   const page = pageAtOffset(layout, offset);
   const box = layout.boxes[page - 1];
   const slot = box.height + PDF_VIEWER_PAGE_GAP;
   return { page, fraction: slot > 0 ? (offset - box.top) / slot : 0 };
}

export function offsetForAnchor(layout: PdfLayout, anchor: PdfScrollAnchor): number | null {
   const box = layout.boxes[anchor.page - 1];
   if (!box) {
      return null;
   }
   return box.top + anchor.fraction * (box.height + PDF_VIEWER_PAGE_GAP);
}
